import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';

// Critical tab of the Ordering page — raw materials short for High/Critical
// customer work orders (server: criticalMaterialAlerts.js), plus the per-vendor
// draft worksheets the weekday alert leaves behind. Drafts live in Run only:
// purchasing keys the PO in NetSuite, then marks the worksheet ordered here.

const fmtQty = (n, unit) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}${unit === 'lb' ? ' lb' : ''}`;
const fmtDate = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
const STAGE_META = {
  order_now: { label: 'Order now', cls: 'oil-pill-serious' },
  heads_up: { label: 'Within a week', cls: 'oil-pill-warning' },
};

function DraftCard({ draft, onSaved }) {
  const [lines, setLines] = useState(draft.lines || []);
  const [poNumber, setPoNumber] = useState(draft.ns_po_number || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async (patch) => {
    setSaving(true);
    setError(null);
    try {
      await axios.patch(`/api/critical-materials/drafts/${draft.id}`, patch);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setQty = (sku, quantity) => setLines((ls) => ls.map((l) => (l.sku === sku ? { ...l, quantity } : l)));

  return (
    <div className="oil-panel">
      <div className="oil-panel-head">
        <h3>{draft.vendor_name}</h3>
        <span className="oil-panel-sub">Draft · updated {fmtDate(draft.updated_at)}</span>
      </div>
      <table className="oil-mini-table oil-mini-pad">
        <thead>
          <tr><th>Item</th><th className="r">Qty</th><th>Order by</th><th>For</th></tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.sku}>
              <td>{l.sku}</td>
              <td className="r">
                <input
                  type="number"
                  min="0"
                  value={l.quantity}
                  onChange={(e) => setQty(l.sku, e.target.value)}
                  style={{ width: 90, textAlign: 'right' }}
                />
                {l.unit === 'lb' ? ' lb' : ''}
                {l.note && <div className="oil-mini-sku">{l.note}</div>}
              </td>
              <td>{fmtDate(l.order_by)}</td>
              <td className="oil-mini-sku">
                {(l.customers || []).join(', ')}
                {l.work_orders?.length ? ` · WO ${l.work_orders.join(', ')}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <input
          type="text"
          placeholder="NetSuite PO #"
          value={poNumber}
          onChange={(e) => setPoNumber(e.target.value)}
          style={{ width: 140 }}
        />
        <button
          className="low-stock-refresh"
          disabled={saving || !poNumber.trim()}
          onClick={() => save({ status: 'ordered', ns_po_number: poNumber, lines })}
        >
          Mark ordered in NetSuite
        </button>
        <button className="oil-link-btn" disabled={saving} onClick={() => save({ lines })}>Save quantities</button>
        <button className="oil-link-btn" disabled={saving} onClick={() => save({ status: 'dismissed' })}>Dismiss</button>
        {error && <span className="oil-num-short">{error}</span>}
      </div>
    </div>
  );
}

function CriticalMaterialsTab() {
  const [summary, setSummary] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, d] = await Promise.all([
        axios.get('/api/critical-materials', { timeout: 300000 }),
        axios.get('/api/critical-materials/drafts'),
      ]);
      setSummary(s.data);
      setDrafts(d.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load critical materials');
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadDrafts = useCallback(async () => {
    const d = await axios.get('/api/critical-materials/drafts');
    setDrafts(d.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !summary) return <LoadingSpinner />;
  if (error) return <div className="oil-panel"><span className="oil-num-short">{error}</span></div>;

  const alerts = summary?.alerts || [];
  return (
    <>
      <div className="oil-panel">
        <div className="oil-panel-head">
          <h3>Short for High / Critical customer work orders</h3>
          <span className="oil-panel-sub">
            {summary?.work_orders || 0} priority work orders · minimum quantities only · emailed weekdays 7am
          </span>
          <button className="low-stock-refresh" onClick={load} disabled={loading} style={{ marginLeft: 'auto' }}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {alerts.length === 0 ? (
          <p className="oil-panel-sub">Nothing short for priority customer work orders.</p>
        ) : (
          <table className="oil-list-table">
            <thead>
              <tr>
                <th>Material</th><th>Action</th><th className="r">Qty</th><th>Order by</th>
                <th>Vendor · lead</th><th className="r">Need / on hand / on order</th><th>For</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => {
                const stage = STAGE_META[a.stage];
                return (
                  <tr key={`${a.sku}-${a.kind}`} className="oil-list-row">
                    <td>
                      {a.sku}
                      {a.substitutable && <span className="oil-pill oil-pill-muted" style={{ marginLeft: 6 }}>can substitute</span>}
                    </td>
                    <td>
                      {stage && <span className={`oil-pill ${stage.cls}`}>{stage.label}</span>}{' '}
                      {a.kind === 'buy' ? 'Buy' : 'Expedite'}
                    </td>
                    <td className="r">
                      {fmtQty(a.kind === 'buy' ? a.buy : a.shortage, a.unit)}
                      {a.orderNote && <div className="oil-mini-sku">{a.orderNote}</div>}
                    </td>
                    <td className={a.lateEvenIfOrderedToday ? 'oil-num-short' : ''}>
                      {fmtDate(a.orderBy)}{a.lateEvenIfOrderedToday ? ' · late' : ''}
                    </td>
                    <td className="oil-cust">{a.vendor} · {a.leadDays}d</td>
                    <td className="r">
                      {fmtQty(a.totalDemand, a.unit)} / {fmtQty(a.onHand, a.unit)} / {fmtQty(a.totalOnOrder, a.unit)}
                    </td>
                    <td className="oil-chip-cell">
                      {a.workOrders.slice(0, 3).map((w) => (
                        <span key={w.woNumber} className="oil-chip">WO {w.woNumber} · {w.customer} · due {fmtDate(w.dateRequired)}</span>
                      ))}
                      {a.workOrders.length > 3 && <span className="oil-chip">+{a.workOrders.length - 3}</span>}
                      {a.kind === 'expedite' && (a.latePos || []).map((p) => (
                        <span key={p.poNumber} className="oil-chip">{p.poNumber} lands {fmtDate(p.arrives)}</span>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <h3 style={{ margin: '20px 0 8px' }}>Draft worksheets ({drafts.length})</h3>
      {drafts.length === 0 && <p className="oil-panel-sub">No open drafts. They appear here when an item reaches "Order now".</p>}
      {drafts.map((d) => <DraftCard key={`${d.id}-${d.updated_at}`} draft={d} onSaved={reloadDrafts} />)}
    </>
  );
}

export default CriticalMaterialsTab;
